import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import dayjs from "dayjs";
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  CheckCircle2Icon,
  InfoIcon,
  Loader2Icon,
  PhoneIcon,
  UserIcon,
} from "lucide-react";
import LOGO from "assets/logo.svg";
import { toast } from "utils/toast";

import { useDecodeInviteToken } from "hooks/useDecodeInviteToken";

import TermsAndConditions from "js/components/Auth/TermsAndConditions";
import { Account, AccountData, newUserRequest } from "js/components/types";

import { Button } from "js/components/ui/button";
import { Checkbox } from "js/components/ui/checkbox";
import { DatePicker } from "js/components/ui/date-picker";
import { EmailInput, EmailInputRef } from "js/components/ui/email-input";
import { Input } from "js/components/ui/input";
import { Label } from "js/components/ui/label";
import { PasswordInput } from "js/components/ui/password-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "js/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "js/components/ui/tooltip";

import { clientTypeOptions } from "../../../fixtures/clientTypes";
import { useAppDispatch } from "../../../redux/hooks";
import { registerUser } from "../../../store/features/auth/authActions";
import {
  passwordRules,
  validateClientType,
  validateEmail,
} from "../../../utils/validators/generalValidators";

type CreateUserRequest = Account & AccountData;

export interface SignUpFormData {
  email: string;
  password: string;
  confirm_password: string;
  name: string;
  first_surname: string;
  second_surname?: string;
  gender: string;
  birth_date?: Date;
  phone_number: string;
  optional_email?: string;
  client_type: string;
  referral_code?: string;
  terms: boolean;
}

export const signUpSteps = [
  {
    id: 1,
    title: "Crea tu cuenta",
    description: "Configura tu correo y contraseña",
  },
  {
    id: 2,
    title: "Datos personales",
    description: "Información básica para tu perfil",
  },
  {
    id: 3,
    title: "Verificación",
    description: "Confirma tu correo electrónico",
  },
];

interface SignUpProps {
  currentStep?: number;
  onStepChange?: (step: number) => void;
}

const slideVariants = {
  enterFromRight: {
    x: 30,
    opacity: 0,
  },
  enterFromLeft: {
    x: -30,
    opacity: 0,
  },
  center: {
    x: 0,
    opacity: 1,
  },
  exitToLeft: {
    x: -30,
    opacity: 0,
  },
  exitToRight: {
    x: 30,
    opacity: 0,
  },
};

const SignUp = ({ currentStep = 1, onStepChange }: SignUpProps) => {
  const [direction, setDirection] = useState<"forward" | "backward">("forward");
  const navigate = useNavigate();
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  const [searchParams] = useSearchParams();
  const [showReferralCodeInput, setShowReferralCodeInput] = useState(false);
  const referral_code = searchParams.get("referral_code");

  const emailInputRef = useRef<EmailInputRef>(null);
  const optionalEmailInputRef = useRef<EmailInputRef>(null);

  const { decodedInviteJWT } = useDecodeInviteToken();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm<SignUpFormData>({
    defaultValues: {
      email: decodedInviteJWT.email || "",
      password: "",
      confirm_password: "",
      name: "",
      first_surname: "",
      second_surname: "",
      gender: "",
      phone_number: "",
      optional_email: "",
      client_type: "",
      referral_code: referral_code || "",
      terms: false,
    },
  });

  const password = watch("password");

  useEffect(() => {
    if (decodedInviteJWT.email) {
      setValue("email", decodedInviteJWT.email);
    }
  }, [decodedInviteJWT, setValue]);

  useEffect(() => {
    if (referral_code) {
      setShowReferralCodeInput(true);
    }
  }, [referral_code]);

  const validatePassword = (value: string) => {
    const errors: string[] = [];
    passwordRules.forEach((rule) => {
      if (!rule.pattern.test(value)) {
        errors.push(rule.message);
      }
    });
    return errors.length === 0 || errors.join(". ");
  };

  const handleNextStep = async () => {
    // Validate email using EmailInput ref
    const emailIsValid = await emailInputRef.current?.validate();
    if (!emailIsValid) {
      return;
    }

    const isValid = await trigger(["password", "confirm_password"]);
    if (isValid && onStepChange) {
      setDirection("forward");
      onStepChange(2);
    }
  };

  const handlePrevStep = () => {
    if (onStepChange) {
      setDirection("backward");
      onStepChange(1);
    }
  };

  const onSubmit = async (formValues: SignUpFormData) => {
    // Prevent submission if not on the final step (Enter key on step 1)
    if (currentStep !== 2) {
      return;
    }

    setLoading(true);

    const account: Account = {
      email: formValues.email.toLowerCase(),
      password: formValues.password,
      confirm_password: formValues.confirm_password,
    };

    const account_data: AccountData = {
      name: formValues.name,
      first_surname: formValues.first_surname,
      second_surname: formValues.second_surname,
      phone_number: formValues.phone_number,
      gender: formValues.gender,
      birth_date: formValues.birth_date
        ? dayjs(formValues.birth_date).format("DD/MM/YYYY")
        : undefined,
      optional_email: formValues.optional_email || undefined,
      client_type: formValues.client_type,
    };

    const newUser: newUserRequest = {
      account: account,
      account_data: account_data,
      referral_code: formValues.referral_code || referral_code,
    };

    dispatch(registerUser(newUser) as any)
      .unwrap()
      .then(() => {
        navigate("/verify-account");
      })
      .catch((error: any) => {
        if (error.message && error.data) {
          const errorData = error.data;
          if (errorData.account?.email) {
            toast.error(`${error.message}: ${errorData.account.email[0]}`);
          }
          if (errorData.account?.password) {
            toast.error(errorData.account.password[0]);
          }
        } else {
          toast.error(error.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const PasswordStrengthIndicator = ({ password }: { password: string }) => {
    const checks = passwordRules.map((rule) => ({
      label: rule.message,
      passed: rule.pattern.test(password),
    }));

    if (!password) return null;

    return (
      <div className="mt-2 space-y-1.5">
        {checks.map((check, index) => (
          <div
            key={index}
            className={`flex items-center gap-2 text-xs ${
              check.passed ? "text-green-600" : "text-muted-foreground"
            }`}
          >
            {check.passed ? (
              <CheckCircle2Icon className="h-3.5 w-3.5" />
            ) : (
              <AlertCircleIcon className="h-3.5 w-3.5" />
            )}
            <span>{check.label}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Logo - visible only on mobile */}
      <div className="flex justify-start lg:hidden">
        <a href="https://multiburo.com.mx/">
          <img src={LOGO} alt="Multiburo" className="h-14 w-auto" />
        </a>
      </div>

      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          {currentStep === 1 ? "Crea tu cuenta" : "Datos personales"}
        </h1>
        <p className="text-muted-foreground">
          {currentStep === 1
            ? "Configura tu correo y contraseña para acceder"
            : "Completa tu información personal"}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <AnimatePresence mode="wait" initial={false}>
          {/* Step 1: Account Details */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              className="space-y-4"
              initial={direction === "forward" ? "enterFromRight" : "enterFromLeft"}
              animate="center"
              exit={direction === "forward" ? "exitToLeft" : "exitToRight"}
              variants={slideVariants}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
            {/* Email */}
            <Controller
              name="email"
              control={control}
              rules={{
                required: "El correo es requerido",
              }}
              render={({ field }) => (
                <EmailInput
                  ref={emailInputRef}
                  value={field.value}
                  onChange={field.onChange}
                  label="Correo electrónico"
                  placeholder="tu@correo.com"
                  required
                  disabled={!!decodedInviteJWT.email}
                  showIcon
                  showStatus
                  className="h-11"
                  error={errors.email?.message}
                />
              )}
            />

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña *</Label>
              <Controller
                name="password"
                control={control}
                rules={{
                  required: "La contraseña es requerida",
                  validate: validatePassword,
                }}
                render={({ field }) => (
                  <PasswordInput
                    id="password"
                    placeholder="********"
                    className={`h-11 ${errors.password ? "border-destructive" : ""}`}
                    data-sentry-ignore
                    {...field}
                  />
                )}
              />
              <PasswordStrengthIndicator password={password || ""} />
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirmar contraseña *</Label>
              <Controller
                name="confirm_password"
                control={control}
                rules={{
                  required: "Confirma tu contraseña",
                  validate: (value) =>
                    value === password || "Las contraseñas no coinciden",
                }}
                render={({ field }) => (
                  <PasswordInput
                    id="confirm_password"
                    placeholder="********"
                    className={`h-11 ${errors.confirm_password ? "border-destructive" : ""}`}
                    data-sentry-ignore
                    {...field}
                  />
                )}
              />
              {errors.confirm_password && (
                <p className="text-sm text-destructive">
                  {errors.confirm_password.message}
                </p>
              )}
            </div>

              {/* Continue Button */}
              <Button
                type="button"
                className="h-11 w-full text-base font-semibold"
                onClick={handleNextStep}
              >
                Continuar
              </Button>

              {/* Login Link - Only on Step 1 */}
              <p className="text-center text-sm text-muted-foreground">
                ¿Ya tienes una cuenta?{" "}
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 font-semibold"
                  onClick={() => navigate("/login")}
                >
                  Inicia sesión
                </Button>
              </p>
            </motion.div>
          )}

          {/* Step 2: Personal Info */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              className="space-y-4"
              initial={direction === "forward" ? "enterFromRight" : "enterFromLeft"}
              animate="center"
              exit={direction === "forward" ? "exitToLeft" : "exitToRight"}
              variants={slideVariants}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
            {/* Name Fields */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre *</Label>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: "El nombre es requerido",
                    minLength: {
                      value: 2,
                      message: "Debe contener al menos 2 caracteres",
                    },
                    maxLength: {
                      value: 80,
                      message: "Debe contener máximo 80 caracteres",
                    },
                    pattern: {
                      value: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/,
                      message: "Solo se permiten letras",
                    },
                  }}
                  render={({ field }) => (
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="name"
                        placeholder="Juan"
                        className={`h-11 pl-10 ${errors.name ? "border-destructive" : ""}`}
                        data-sentry-ignore
                        {...field}
                      />
                    </div>
                  )}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">
                    {errors.name.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="first_surname">Apellido paterno *</Label>
                <Controller
                  name="first_surname"
                  control={control}
                  rules={{
                    required: "El apellido es requerido",
                    minLength: {
                      value: 2,
                      message: "Debe contener al menos 2 caracteres",
                    },
                    maxLength: {
                      value: 80,
                      message: "Debe contener máximo 80 caracteres",
                    },
                    pattern: {
                      value: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/,
                      message: "Solo se permiten letras",
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      id="first_surname"
                      placeholder="Pérez"
                      className={`h-11 ${errors.first_surname ? "border-destructive" : ""}`}
                      data-sentry-ignore
                      {...field}
                    />
                  )}
                />
                {errors.first_surname && (
                  <p className="text-sm text-destructive">
                    {errors.first_surname.message}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="second_surname">Apellido materno</Label>
              <Controller
                name="second_surname"
                control={control}
                rules={{
                  minLength: {
                    value: 2,
                    message: "Debe contener al menos 2 caracteres",
                  },
                  maxLength: {
                    value: 80,
                    message: "Debe contener máximo 80 caracteres",
                  },
                  pattern: {
                    value: /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]*$/,
                    message: "Solo se permiten letras",
                  },
                }}
                render={({ field }) => (
                  <Input
                    id="second_surname"
                    placeholder="García"
                    className={`h-11 ${errors.second_surname ? "border-destructive" : ""}`}
                    data-sentry-ignore
                    {...field}
                  />
                )}
              />
              {errors.second_surname && (
                <p className="text-sm text-destructive">
                  {errors.second_surname.message}
                </p>
              )}
            </div>

            {/* Gender and Birth Date */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="gender">Género *</Label>
                <Controller
                  name="gender"
                  control={control}
                  rules={{ required: "Selecciona tu género" }}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger
                        className={`h-11 w-full ${errors.gender ? "border-destructive" : ""}`}
                      >
                        <SelectValue placeholder="Selecciona tu género" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Hombre</SelectItem>
                        <SelectItem value="F">Mujer</SelectItem>
                        <SelectItem value="N">Prefiero no decir</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.gender && (
                  <p className="text-sm text-destructive">
                    {errors.gender.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="birth_date">Fecha de nacimiento *</Label>
                <Controller
                  name="birth_date"
                  control={control}
                  rules={{
                    required: "La fecha de nacimiento es requerida",
                    validate: (value) => {
                      if (!value) return true;
                      const age = dayjs().diff(dayjs(value), "year");
                      if (age < 18) return "Debes ser mayor de 18 años";
                      if (age > 120) return "Fecha inválida";
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <DatePicker
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="DD/MM/YYYY"
                      maxDate={new Date()}
                      fromYear={1900}
                      toYear={new Date().getFullYear() - 18}
                      hasError={!!errors.birth_date}
                      data-sentry-ignore
                    />
                  )}
                />
                {errors.birth_date && (
                  <p className="text-sm text-destructive">
                    {errors.birth_date.message}
                  </p>
                )}
              </div>
            </div>

            {/* Phone and Client Type */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="phone_number">Celular *</Label>
                <Controller
                  name="phone_number"
                  control={control}
                  rules={{
                    required: "El teléfono es requerido",
                    pattern: {
                      value: /^\d{10}$/,
                      message: "Ingresa 10 dígitos",
                    },
                  }}
                  render={({ field }) => (
                    <div className="relative">
                      <PhoneIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="phone_number"
                        placeholder="5512345678"
                        maxLength={10}
                        className={`h-11 pl-10 ${errors.phone_number ? "border-destructive" : ""}`}
                        data-sentry-ignore
                        {...field}
                      />
                    </div>
                  )}
                />
                {errors.phone_number && (
                  <p className="text-sm text-destructive">
                    {errors.phone_number.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="client_type">Perfil de usuario *</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-4 w-4 cursor-help text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>
                          Selecciona el tipo de cliente que mejor describa tu uso
                          de la plataforma.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Controller
                  name="client_type"
                  control={control}
                  rules={{
                    required: "Selecciona tu perfil de usuario",
                    validate: async (value) => {
                      if (!value) return true;
                      try {
                        await validateClientType(null, value);
                        return true;
                      } catch (error: any) {
                        return error.message;
                      }
                    },
                  }}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <SelectTrigger
                        className={`h-11 w-full ${errors.client_type ? "border-destructive" : ""}`}
                      >
                        <SelectValue placeholder="Selecciona una opción" />
                      </SelectTrigger>
                      <SelectContent>
                        {clientTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.client_type && (
                  <p className="text-sm text-destructive">
                    {errors.client_type.message}
                  </p>
                )}
              </div>
            </div>

            {/* Optional Email */}
            <Controller
              name="optional_email"
              control={control}
              rules={{
                validate: async (value) => {
                  if (!value || value.trim() === "") return true;
                  try {
                    await validateEmail(null, value);
                    return true;
                  } catch (error: any) {
                    return error.message;
                  }
                },
              }}
              render={({ field }) => (
                <EmailInput
                  ref={optionalEmailInputRef}
                  value={field.value || ""}
                  onChange={field.onChange}
                  label="Email secundario"
                  placeholder="email.secundario@correo.com"
                  showIcon={false}
                  showStatus={false}
                  className="h-11"
                  error={errors.optional_email?.message}
                />
              )}
            />

            {/* Referral Code */}
            {!referral_code && (
              <div className="space-y-2">
                {!showReferralCodeInput ? (
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-sm"
                    onClick={() => setShowReferralCodeInput(true)}
                  >
                    ¿Tienes un código de referido?
                  </Button>
                ) : (
                  <>
                    <Label htmlFor="referral_code">Código de referido</Label>
                    <Controller
                      name="referral_code"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="referral_code"
                          placeholder="ABC123"
                          maxLength={10}
                          className="h-11"
                          {...field}
                        />
                      )}
                    />
                  </>
                )}
              </div>
            )}

            {/* Terms Card */}
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="flex items-start space-x-3">
                <Controller
                  name="terms"
                  control={control}
                  rules={{
                    validate: (value) =>
                      value === true || "Debes aceptar los términos y condiciones",
                  }}
                  render={({ field }) => (
                    <Checkbox
                      id="terms"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="mt-0.5"
                    />
                  )}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="terms"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Acepto los términos y condiciones
                  </label>
                  <p className="text-sm text-muted-foreground">
                    Al registrarte, aceptas nuestros{" "}
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-sm"
                      onClick={() => setModalVisible(true)}
                    >
                      términos de servicio
                    </Button>{" "}
                    y{" "}
                    <a
                      href="https://multiburo.com.mx/privacidad"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-primary underline-offset-4 hover:underline"
                    >
                      política de privacidad
                    </a>
                    .
                  </p>
                </div>
              </div>
              {errors.terms && (
                <p className="mt-2 text-sm text-destructive">{errors.terms.message}</p>
              )}
            </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 sm:w-auto"
                  onClick={handlePrevStep}
                >
                  <ArrowLeftIcon className="mr-2 h-4 w-4" />
                  Anterior
                </Button>
                <Button
                  type="submit"
                  className="h-11 flex-1 text-base font-semibold"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                      Creando cuenta...
                    </>
                  ) : (
                    "Crear cuenta"
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </form>

      <TermsAndConditions
        open={modalVisible}
        onClose={() => setModalVisible(false)}
      />
    </div>
  );
};

export default SignUp;
